import type {
  DailyDeliveryResponse,
  FilterState,
} from "features/daily-delivery/types";
import type { IOption } from "types/option";
import Fetch from "utils/fetch";
import { generateQueryParams } from "utils/custom-functions";

/**
 * Builds query parameters object from FilterState
 * Maps FilterState properties to API expected parameter names
 * @param filters - FilterState object with filter parameters
 * @returns Object with API parameter names ready for generateQueryParams
 */
const buildQueryParamsObject = (filters: FilterState): Record<string, any> => {
  const params: Record<string, any> = {};

  // Map date fields to API expected names
  if (filters._minDeliveryDate) {
    params.DateFrom = filters._minDeliveryDate;
  }
  if (filters._maxDeliveryDate) {
    params.DateTo = filters._maxDeliveryDate;
  }

  // Add FamilyIds (comma-separated string)
  if (filters.FamilyIds) {
    params.FamilyIds = filters.FamilyIds;
  }

  // Add BreweryIds array (will generate multiple params: BreweryIds=1&BreweryIds=2)
  if (filters.BreweryIds && filters.BreweryIds.length > 0) {
    params.BreweryIds = filters.BreweryIds;
  }

  // Add Status array (will generate multiple params: Status=0&Status=1)
  if (filters.Status && filters.Status.length > 0) {
    params.Status = filters.Status;
  }

  return params;
};

/**
 * Retrieves daily deliveries data based on filters
 * @param filters - FilterState object containing all filter parameters
 * @returns Promise with array of DailyDeliveryResponse or null
 */
export const getDailyDeliveriesAsync = async (
  filters: FilterState
): Promise<DailyDeliveryResponse[] | null> => {
  const paramsObject = buildQueryParamsObject(filters);
  const queryString = generateQueryParams(paramsObject);
  const response = await Fetch.get(`/daily-deliveries?${queryString}`);
  return response?.data ?? null;
};

/**
 * Retrieves available status options for Daily Delivery filters
 * @returns Promise with array of IOption (Id, Description)
 */
export const getDailyDeliveryStatusesAsync = async (): Promise<IOption[]> => {
  const response = await Fetch.get("/daily-deliveries:status");
  return response?.data ?? [];
};

export const getCardDetailForDailyDelivery = (familyId: number, date: string, businessUnitId: number) =>
  Fetch.get(`/daily-deliveries/by-businessunit-family?businessUnitId=${businessUnitId}&familyId=${familyId}&date=${date}`).then(({ data }) => data);
